import {
  InputAdornment,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
  Typography,
  styled
} from '@mui/material';
import { ReactElement, KeyboardEvent, useState, useEffect, FocusEvent, ChangeEvent } from 'react';
import { clsx } from 'clsx';
import { getDataId } from '../../shared/utils/dataId';
import { Icon } from '../../icon/Icon';
import { numberValidation } from '../../shared/utils/input';

interface TextFieldAdditionalProps {
  dataId: string;
  prefixIcon?: string;
  prefixText?: string;
  prefixElement?: ReactElement;
  suffixIcon?: string;
  suffixText?: string;
  suffixElement?: ReactElement;
  forceActiveLegend?: boolean;
  resize?: string;
  customLabelOffset?: number;
  disableDecimal?: boolean;
  uncontrolled?: boolean;
}

export type TextFieldProps = MuiTextFieldProps & TextFieldAdditionalProps;

export const TextField = styled(
  ({
    dataId,
    prefixIcon,
    prefixText,
    prefixElement,
    suffixIcon,
    suffixText,
    suffixElement,
    forceActiveLegend,
    disableDecimal,
    InputProps,
    uncontrolled = false,
    ...props
  }: TextFieldProps) => {
    const inputDataId = getDataId('input')(dataId)();

    const [value, setValue] = useState<string | number | null>(null);
    const [fieldFocused, setFieldFocused] = useState(false);
    const [labelLegendActive, setLabelLegendActive] = useState(!!value);
    const prefix = forceActiveLegend ? false : prefixIcon || prefixText || prefixElement;

    useEffect(() => {
      if (props.inputProps && typeof props.inputProps.value !== 'undefined') {
        sanitizeAndSetValue(props.inputProps.value);
      }
    }, [props.inputProps]);

    useEffect(() => {
      if (fieldFocused) {
        setLabelLegendActive(true);
      } else {
        setLabelLegendActive(!!value);
      }
    }, [value]);

    useEffect(() => {
      if (typeof props?.value !== 'undefined' && props.value !== null) {
        sanitizeAndSetValue(props.value as string);
      }
    }, [props.value]);

    useEffect(() => {
      if (typeof props?.defaultValue !== 'undefined' && props.value !== null) {
        sanitizeAndSetValue(props.defaultValue as string);
      }
    }, [props.defaultValue]);

    const sanitizeAndSetValue = (_value: string | number) => {
      if (props.type === 'number' && numberValidation(_value.toString()) && disableDecimal) {
        setValue(Math.floor(_value as number));
      } else if (props.type === 'number' && !numberValidation(_value.toString())) {
        setValue('');
      } else {
        setValue(_value);
      }
    };

    const onBlurEvent = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFieldFocused(false);

      if (prefix) {
        setLabelLegendActive(!!value);
      }

      if (props.onBlur) {
        props.onBlur(event);
      }
    };

    const onFocusEvent = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFieldFocused(true);

      if (prefix) {
        setLabelLegendActive(true);
      }

      if (props.onFocus) {
        props.onFocus(event);
      }
    };

    const onChangeEvent = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (props?.type === 'number' && !numberValidation(event.target.value)) {
        return;
      }

      if (props?.type === 'number' && disableDecimal && event.target.value.includes('.')) {
        return;
      }

      setValue(event.target.value);

      if (props.onChange) {
        props.onChange(event);
      }
    };

    const getInputLabelProps = () => {
      let classes = '';

      if (!labelLegendActive) {
        classes = prefixIcon ? 'custom-icon-shrinkable' : 'custom-shrinkable';
      }

      return {
        className: classes,
        shrink: labelLegendActive
      };
    };

    let conditionalProps = {};

    if (props?.type === 'number') {
      conditionalProps = {
        onWheel: (e: { target: { blur: VoidFunction } }) => e.target.blur(),
        onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
          if (e.keyCode === 38 || e.keyCode === 40) {
            e.preventDefault();
          }

          if (props.onKeyDown) {
            props.onKeyDown(e);
          }
        }
      };
    }

    let InputPropsFinal = InputProps;

    const getAdornmentElement = (
      adornmentPosition: 'start' | 'end',
      adornment: string | undefined
    ) => (
      <InputAdornment position={adornmentPosition}>
        <Typography
          variant='body2'
          className={clsx('hd-adornment', props.disabled && 'hd-adornment-disabled')}
        >
          {(prefixIcon && adornment && adornmentPosition === 'start') ||
          (suffixIcon && adornment && adornmentPosition === 'end') ? (
            <Icon dataId={adornmentPosition} name={adornment} />
          ) : (
            adornment
          )}
        </Typography>
      </InputAdornment>
    );

    if (prefixIcon || prefixText || prefixElement) {
      InputPropsFinal = {
        ...InputPropsFinal,
        startAdornment: prefixElement || getAdornmentElement('start', prefixIcon || prefixText)
      };
    }

    if (suffixIcon || suffixElement || suffixText) {
      InputPropsFinal = {
        ...InputPropsFinal,
        endAdornment: suffixElement || getAdornmentElement('end', suffixIcon || suffixText)
      };
    }

    /*
      date picker need uncontrolled input to let mui handle the  date formatting
    */
    let inputPropsFinal = {};
    if (uncontrolled) {
      inputPropsFinal = {
        ...props.inputProps,
        'data-id': inputDataId
      };
    } else {
      inputPropsFinal = {
        ...props.inputProps,
        value: value ?? '',
        'data-id': inputDataId
      };
    }

    return (
      <MuiTextField
        {...props}
        {...conditionalProps}
        type={props.type === 'number' ? 'string' : props.type}
        InputProps={InputPropsFinal}
        /* eslint-disable-next-line react/jsx-no-duplicate-props */
        inputProps={inputPropsFinal}
        placeholder={prefix && !labelLegendActive ? '' : props.placeholder}
        InputLabelProps={prefix ? getInputLabelProps() : props.InputLabelProps}
        onBlur={onBlurEvent}
        onFocus={onFocusEvent}
        onChange={onChangeEvent}
      />
    );
  }
)(props => ({
  width: '100%',
  borderRadius: 'var(--border-radius-6)',
  '.MuiOutlinedInput-notchedOutline': {
    zIndex: 1,
    borderColor: 'var(--divider-color)',
    fontSize: 'calc(var(--font-size-caption) / .75)',
    fontWeight: 'var(--font-weight-normal)',
    padding: '0 var(--spacer-16) 0 14px'
  },
  '.MuiOutlinedInput-root': {
    color: 'var(--text-default-color)',
    fontSize: 'var(--font-size-body)',
    fontFamily: 'var(--font-family)',
    fontWeight: 'var(--font-weight-normal)',
    borderRadius: 'var(--border-radius-6)',
    backgroundColor: 'var(--surface-bg-color)',
    width: '100%',
    '&.MuiInputBase-adornedStart': {
      paddingLeft: 'var(--spacer-16)',
      '.MuiOutlinedInput-input, .MuiOutlinedInput-input.MuiInputBase-inputSizeSmall': {
        paddingLeft: '0'
      }
    },
    '&.MuiInputBase-adornedEnd': {
      paddingRight: 'var(--spacer-16)',
      '.MuiOutlinedInput-input, .MuiOutlinedInput-input.MuiInputBase-inputSizeSmall': {
        paddingRight: '0'
      }
    },
    '.MuiOutlinedInput-input': {
      zIndex: 2,
      height: 'var(--line-height-body)',
      fontWeight: 'var(--font-weight-medium)',
      padding: 'var(--spacer-16)',
      '&.MuiInputBase-inputSizeSmall': {
        padding: 'var(--spacer-8) var(--spacer-16)'
      },
      '&::placeholder': {
        opacity: 1,
        color: 'var(--text-secondary-color)',
        fontWeight: 'var(--font-weight-normal)'
      },
      '&.MuiSelect-select': {
        lineHeight: 'var(--line-height-body)'
      }
    },
    '&.MuiInputBase-colorSuccess:not(.Mui-disabled)': {
      '.MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--success-color)'
      },
      '&:hover, &.Mui-focused': {
        '.MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--success-color)'
        }
      }
    },
    '&.Mui-error': {
      '.MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--error-color)'
      },
      '&:hover, &.Mui-focused': {
        '.MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--error-color)'
        }
      }
    },
    '&:hover, &.Mui-focused': {
      '.MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--text-default-color)',
        borderWidth: '1px'
      }
    },
    '&.Mui-disabled': {
      '.MuiOutlinedInput-notchedOutline': {
        borderColor: 'transparent',
        backgroundColor: 'var(--default-bg-color)'
      }
    }
  },
  '.MuiInputLabel-outlined': {
    zIndex: 3,
    color: 'var(--text-secondary-color)',
    lineHeight: 'var(--line-height-body)',
    fontSize: 'var(--font-size-body)',
    fontFamily: 'var(--font-family)',
    fontWeight: 'var(--font-weight-medium)',
    transform: 'translate(var(--spacer-16), var(--spacer-16)) scale(1)',
    '&.MuiInputLabel-sizeSmall': {
      transform: 'translate(var(--spacer-16), var(--spacer-8)) scale(1)'
    },
    '&.MuiInputLabel-shrink': {
      '--scale': 'var(--font-size-caption-body-ratio)',
      '--line-height': 'calc(var(--scale) * var(--line-height-body))',
      '--translate-y': 'calc(var(--line-height) * -.5)',
      transform: 'translate(var(--spacer-16), var(--translate-y)) scale(var(--scale))',
      color: 'var(--text-secondary-color)'
    },
    '&.MuiInputLabel-shrink + .MuiOutlinedInput-root legend span': {
      fontWeight: 'var(--font-weight-medium)',
      padding: 0,
      paddingRight: '2px'
    },
    '&.custom-shrinkable': {
      transform: `translate(${
        Number.isInteger(props.customLabelOffset) ? props.customLabelOffset : 42
      }px, var(--spacer-16)) scale(1)`,
      '&.MuiInputLabel-sizeSmall': {
        transform: `translate(${
          Number.isInteger(props.customLabelOffset) ? props.customLabelOffset : 42
        }px, var(--spacer-8)) scale(1)`
      }
    },
    '&.custom-icon-shrinkable': {
      transform: 'translate(36px, var(--spacer-16)) scale(1)',
      '&.MuiInputLabel-sizeSmall': {
        transform: 'translate(36px, var(--spacer-8)) scale(1)'
      }
    },
    '.MuiFormLabel-asterisk.Mui-error': {
      color: 'var(--error-color)'
    }
  },
  '.MuiFormHelperText-root': {
    color: 'var(--text-secondary-color)',
    fontWeight: 'var(--font-weight-medium)',
    lineHeight: 'var(--line-height-caption)',
    marginTop: 'var(--spacer-4)',
    marginLeft: 'calc(var(--spacer-8) + .8px)',
    marginRight: 'calc(var(--spacer-8) + .8px)',
    '&.Mui-error': {
      color: 'var(--error-color)'
    }
  },
  '.hd-adornment': {
    display: 'flex',
    alignItems: 'center',
    '&.hd-adornment-disabled': {
      color: 'var(--text-disabled)'
    }
  }
}));
